/**
 * Renata Oportunidades Dashboard — FASE 5 Operational Intelligence
 * /app/renata/insights
 */
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import {
  Lightbulb, RefreshCw, AlertTriangle, CheckCircle2,
  Info, TrendingDown, TrendingUp, Clock, X, ChevronRight,
  BarChart2, Calendar, Activity, Zap, Bot,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { insightActionToTaskId, buildTaskChatUrl } from "@/lib/renata/tasks/from-insight";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { toast } from "sonner";
import type {
  InsightInstance, BusinessHealthScore, BusinessBrief, BriefPeriod,
  InsightCategory, InsightSeverity, InsightStatus,
} from "@/lib/renata/insights/types";

export const Route = createFileRoute("/app/renata/insights")({
  component: RenataOportunidadesPage,
  head: () => ({ meta: [{ title: "Oportunidades — Renata — Structura Market" }] }),
});

// ── Helpers ───────────────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<InsightSeverity, { icon: React.ComponentType<{ className?: string }>; label: string; className: string }> = {
  critical: { icon: AlertTriangle, label: "Crítico", className: "text-red-600 bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800 dark:text-red-400" },
  warning:  { icon: TrendingDown, label: "Atención", className: "text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-400" },
  success:  { icon: CheckCircle2, label: "Positivo", className: "text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-400" },
  info:     { icon: Info,          label: "Información",    className: "text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800 dark:text-blue-400" },
};

const CATEGORY_LABELS: Record<string, string> = {
  ventas: "Ventas", inventario: "Inventario", compras: "Compras", clientes: "Clientes",
  marketing: "Marketing", logistica: "Logística", finanzas: "Finanzas", caja: "Caja",
  reservas: "Reservas", produccion: "Producción", bundles: "Packs", promociones: "Promociones",
  ia: "IA", sistema: "Sistema",
};

function HealthRing({ score }: { score: number }) {
  const color = score >= 70 ? "text-emerald-500" : score >= 50 ? "text-amber-500" : "text-red-500";
  const label = score >= 70 ? "Saludable" : score >= 50 ? "Regular" : "Crítico";
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={cn("text-4xl font-bold tabular-nums", color)}>{score}</div>
      <div className="text-xs text-muted-foreground">/ 100</div>
      <Badge variant="outline" className={cn("text-xs", color)}>{label}</Badge>
    </div>
  );
}

function InsightCard({
  insight,
  onStatusChange,
}: {
  insight: InsightInstance;
  onStatusChange: (id: string, status: InsightStatus) => void;
}) {
  const cfg = SEVERITY_CONFIG[insight.severity];
  const Icon = cfg.icon;
  const [updating, setUpdating] = useState(false);

  async function update(status: InsightStatus) {
    setUpdating(true);
    onStatusChange(insight.id, status);
    setUpdating(false);
  }

  return (
    <div className={cn("rounded-xl border p-4 transition-all", cfg.className)}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium uppercase tracking-wide opacity-70">
              {CATEGORY_LABELS[insight.category] ?? insight.category}
            </span>
            <Badge variant="outline" className="text-[10px] py-0">
              {cfg.label}
            </Badge>
            {insight.generated_by_agent && (
              <Badge variant="secondary" className="text-[10px] py-0 gap-1">
                <Bot className="h-2.5 w-2.5" />
                {insight.generated_by_agent}
              </Badge>
            )}
          </div>
          <p className="mt-1 text-sm font-medium leading-snug">{insight.title}</p>
          <p className="mt-1 text-xs opacity-80">{insight.summary}</p>

          {/* Evidence metrics */}
          {insight.evidence.metrics && Object.keys(insight.evidence.metrics).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(insight.evidence.metrics).slice(0, 4).map(([k, v]) => (
                <span key={k} className="rounded bg-black/5 dark:bg-white/5 px-1.5 py-0.5 text-[10px]">
                  {k}: <strong>{String(v)}</strong>
                </span>
              ))}
            </div>
          )}

          {/* Evidence samples */}
          {insight.evidence.samples && insight.evidence.samples.length > 0 && (
            <div className="mt-1 text-[10px] opacity-70">
              {insight.evidence.samples.slice(0, 3).join(" · ")}
            </div>
          )}

          {/* Actions */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {insight.actions.map((action, i) => {
              const taskId = insightActionToTaskId({ skill_id: action.skill_id, action: action.action });
              return (
                <span key={i} className="flex items-center gap-1">
                  <a
                    href={action.href ?? "#"}
                    className="flex items-center gap-1 text-xs underline-offset-2 hover:underline opacity-90"
                  >
                    {action.label}
                    <ChevronRight className="h-3 w-3" />
                  </a>
                  {taskId && (
                    <Link
                      to={buildTaskChatUrl(taskId) as "/app/renata/chat"}
                      search={{ task: taskId } as never}
                      className="flex items-center gap-1 text-xs text-primary hover:underline ml-1"
                    >
                      <Zap className="h-3 w-3" />
                      Iniciar tarea
                    </Link>
                  )}
                </span>
              );
            })}

            {/* Status actions */}
            <div className="ml-auto flex items-center gap-1">
              {insight.status === "open" && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-[10px] gap-1"
                    disabled={updating}
                    onClick={() => void update("dismissed")}
                  >
                    <X className="h-3 w-3" />
                    Ignorar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-[10px] gap-1"
                    disabled={updating}
                    onClick={() => void update("applied")}
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    Aplicado
                  </Button>
                </>
              )}
              {insight.status !== "open" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-[10px]"
                  disabled={updating}
                  onClick={() => void update("open")}
                >
                  Reabrir
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BriefTab({ brief }: { brief: BusinessBrief }) {
  function money(n: number) {
    return `$${n.toLocaleString("es-AR", { minimumFractionDigits: 0 })}`;
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground italic">{brief.summary_text}</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Pedidos", value: String(brief.sales.orders) },
          { label: "Ingresos", value: money(brief.sales.revenue) },
          { label: "Ticket prom.", value: money(brief.sales.avg_ticket) },
          { label: "Alertas stock", value: String(brief.stock_alerts) },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-lg border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">{kpi.label}</div>
            <div className="text-xl font-semibold tabular-nums">{kpi.value}</div>
          </div>
        ))}
      </div>
      {brief.top_insights.length > 0 && (
        <div>
          <p className="text-xs font-medium mb-2">Principales oportunidades del período:</p>
          <div className="space-y-1">
            {brief.top_insights.map((ins) => (
              <div key={ins.id} className="flex items-center gap-2 text-sm">
                {ins.severity === "critical" ? (
                  <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                ) : (
                  <Info className="h-3.5 w-3.5 text-amber-500" />
                )}
                <span>{ins.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function RenataOportunidadesPage() {
  const tenantId = useWorkspaceTenantId();
  const [insights, setOportunidades] = useState<InsightInstance[]>([]);
  const [health, setHealth] = useState<BusinessHealthScore | null>(null);
  const [brief, setBrief] = useState<BusinessBrief | null>(null);
  const [briefPeriod, setBriefPeriod] = useState<BriefPeriod>("weekly");
  const [filterCategory, setFilterCategory] = useState<InsightCategory | "all">("all");
  const [filterSeverity, setFilterSeverity] = useState<InsightSeverity | "all">("all");
  const [filterStatus, setFilterStatus] = useState<InsightStatus | "open">("open");
  const [activeTab, setActiveTab] = useState<"insights" | "brief" | "health">("insights");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);

  const headers = useCallback(
    () => clientRequestHeaders(tenantId ?? ""),
    [tenantId],
  );

  const loadOportunidades = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: filterStatus, limit: "50" });
      if (filterCategory !== "all") params.set("category", filterCategory);
      if (filterSeverity !== "all") params.set("severity", filterSeverity);
      const res = await fetch(`/api/renata/insights?${params}`, {
        credentials: "include",
        headers: headers(),
      });
      const data = (await res.json()) as { items: InsightInstance[]; total: number };
      setOportunidades(data.items ?? []);
      setTotal(data.total ?? 0);
    } finally {
      setLoading(false);
    }
  }, [tenantId, filterCategory, filterSeverity, filterStatus, headers]);

  const loadHealth = useCallback(async () => {
    if (!tenantId) return;
    const res = await fetch("/api/renata/insights?health=1", {
      credentials: "include",
      headers: headers(),
    });
    const data = (await res.json()) as { health: BusinessHealthScore };
    setHealth(data.health ?? null);
  }, [tenantId, headers]);

  const loadBrief = useCallback(async (period: BriefPeriod) => {
    if (!tenantId) return;
    const res = await fetch(`/api/renata/insights?brief=${period}`, {
      credentials: "include",
      headers: headers(),
    });
    const data = (await res.json()) as { brief: BusinessBrief };
    setBrief(data.brief ?? null);
  }, [tenantId, headers]);

  useEffect(() => {
    if (!tenantId) return;
    void Promise.all([loadOportunidades(), loadHealth()]);
  }, [tenantId, loadOportunidades, loadHealth]);

  useEffect(() => {
    if (activeTab === "brief") void loadBrief(briefPeriod);
  }, [activeTab, briefPeriod, loadBrief]);

  async function handleRefresh() {
    if (!tenantId) return;
    setRefreshing(true);
    try {
      const res = await fetch("/api/renata/insights?refresh=1", {
        method: "POST",
        credentials: "include",
        headers: { ...headers(), "Content-Type": "application/json" },
      });
      const data = (await res.json()) as { generated: number; health_score: number };
      toast.success(`${data.generated} oportunidades generadas · Estado del negocio: ${data.health_score}/100`);
      await Promise.all([loadOportunidades(), loadHealth()]);
    } catch {
      toast.error("Error al actualizar oportunidades");
    } finally {
      setRefreshing(false);
    }
  }

  async function handleStatusChange(id: string, status: InsightStatus) {
    if (!tenantId) return;
    try {
      await fetch("/api/renata/insights", {
        method: "PATCH",
        credentials: "include",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({ id, status }),
      });
      setOportunidades((prev) =>
        status === "open"
          ? prev.map((i) => (i.id === id ? { ...i, status } : i))
          : prev.filter((i) => i.id !== id),
      );
      if (status !== "open") setTotal((t) => Math.max(0, t - 1));
    } catch {
      toast.error("Error al actualizar la oportunidad");
    }
  }

  const tabs = [
    { id: "insights" as const, label: "Oportunidades", icon: Lightbulb },
    { id: "brief" as const, label: "Resumen", icon: Calendar },
    { id: "health" as const, label: "Estado del negocio", icon: Activity },
  ];

  const categories = [
    "all", "ventas", "inventario", "compras", "clientes", "marketing",
    "logistica", "finanzas", "bundles", "promociones", "ia", "sistema",
  ] as const;

  const severities = ["all", "critical", "warning", "success", "info"] as const;
  const statuses = ["open", "dismissed", "applied", "resolved"] as const;

  if (!tenantId) return null;

  return (
    <div className="flex min-w-0 flex-col">
      {/* Header */}
      <div className="shrink-0 border-b px-6 py-4">
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-base font-semibold">
              <Lightbulb className="h-4 w-4 text-primary" />
              Oportunidades Operacionales
            </h1>
            <p className="text-xs text-muted-foreground">
              {total} insight{total !== 1 ? "s" : ""} activo{total !== 1 ? "s" : ""} · Análisis basado en datos reales
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={() => void handleRefresh()}
            disabled={refreshing}
          >
            <RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
            Actualizar
          </Button>
        </div>

        {/* Tabs */}
        <nav className="mt-3 flex min-w-0 flex-wrap gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors",
                activeTab === tab.id
                  ? "bg-violet-100 text-violet-700 font-medium dark:bg-violet-900/30 dark:text-violet-300"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="min-w-0 p-4 sm:p-6">

        {/* ── Oportunidades tab ── */}
        {activeTab === "insights" && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              {/* Category */}
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value as InsightCategory | "all")}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>{c === "all" ? "Todas las categorías" : CATEGORY_LABELS[c] ?? c}</option>
                ))}
              </select>

              {/* Severity */}
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value as InsightSeverity | "all")}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              >
                {severities.map((s) => (
                  <option key={s} value={s}>{s === "all" ? "Todas las severidades" : SEVERITY_CONFIG[s as InsightSeverity]?.label ?? s}</option>
                ))}
              </select>

              {/* Status */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as InsightStatus)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>{s === "open" ? "Abiertos" : s === "dismissed" ? "Ignorados" : s === "applied" ? "Aplicados" : "Resueltos"}</option>
                ))}
              </select>
            </div>

            {loading ? (
              <div className="flex h-32 items-center justify-center">
                <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : insights.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 gap-3 text-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                <p className="text-sm font-medium">Todo bajo control</p>
                <p className="text-xs text-muted-foreground max-w-xs">
                  No hay insights en este filtro. Actualizá para ver los más recientes.
                </p>
                <Button size="sm" variant="outline" onClick={() => void handleRefresh()}>
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                  Actualizar ahora
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {insights.map((ins) => (
                  <InsightCard
                    key={ins.id}
                    insight={ins}
                    onStatusChange={(id, status) => void handleStatusChange(id, status)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Brief tab ── */}
        {activeTab === "brief" && (
          <div className="space-y-4">
            <div className="flex gap-1">
              {(["daily", "weekly", "monthly"] as BriefPeriod[]).map((p) => (
                <button
                  key={p}
                  onClick={() => setBriefPeriod(p)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-xs transition-colors",
                    briefPeriod === p
                      ? "bg-violet-100 text-violet-700 font-medium dark:bg-violet-900/30 dark:text-violet-300"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {p === "daily" ? "Diario" : p === "weekly" ? "Semanal" : "Mensual"}
                </button>
              ))}
            </div>
            {brief ? (
              <BriefTab brief={brief} />
            ) : (
              <div className="flex h-32 items-center justify-center">
                <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        )}

        {/* ── Estado del negocio ── */}
        {activeTab === "health" && (
          <div className="space-y-6">
            {health ? (
              <>
                <div className="flex items-center gap-6">
                  <HealthRing score={health.total} />
                  <div>
                    <p className="text-sm font-medium">Estado del negocio</p>
                    <p className="text-xs text-muted-foreground max-w-xs">
                      Calculado en base a ventas, inventario, clientes, operación, IA y marketing.
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Actualizado: {new Date(health.computed_at).toLocaleString("es-AR")}
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {health.dimensions.map((dim) => (
                    <div key={dim.key} className="rounded-lg border bg-muted/20 p-3 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold">{dim.label}</span>
                        <span className={cn(
                          "text-sm font-bold tabular-nums",
                          dim.score >= 70 ? "text-emerald-500" : dim.score >= 50 ? "text-amber-500" : "text-red-500",
                        )}>
                          {dim.score}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className={cn(
                            "h-full rounded-full transition-all",
                            dim.score >= 70 ? "bg-emerald-500" : dim.score >= 50 ? "bg-amber-500" : "bg-red-500",
                          )}
                          style={{ width: `${dim.score}%` }}
                        />
                      </div>
                      {dim.criteria && (
                        <p className="text-[10px] text-muted-foreground leading-snug">{dim.criteria}</p>
                      )}
                      {dim.notes && (
                        <p className="text-[10px] text-foreground/70 leading-snug">{dim.notes}</p>
                      )}
                      {dim.improvement && (
                        <p className="text-[10px] font-medium text-primary leading-snug">→ {dim.improvement}</p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex h-32 items-center justify-center">
                <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
